import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { AuthContext, type AuthState } from './authContextValue';

import { setAccessToken, setSessionLostHandler } from '@/lib/apiClient';
import { authApi, type Session } from '@/lib/authApi';

/**
 * Estado de autenticação da aplicação.
 *
 * <p>
 * Ao montar, tenta restaurar a sessão pelo cookie de refresh. É o que faz o
 * usuário continuar logado depois de um F5 <b>sem</b> guardar token em
 * <code>localStorage</code>, onde um XSS o alcançaria (docs/12 §A05).
 * </p>
 * <p>
 * <code>isRestoring</code> existe para que a rota protegida não redirecione
 * para o login durante essa tentativa. Sem ele, todo recarregamento piscaria a
 * tela de acesso antes de voltar — e pareceria que a sessão caiu.
 * </p>
 */
export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);

  const apply = useCallback((next: Session | null) => {
    setSession(next);
    setAccessToken(next?.accessToken ?? null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Sessão perdida no meio do uso (refresh recusado, replay detectado):
    // limpa o estado para o app voltar ao login sem recarregar a página.
    setSessionLostHandler(() => {
      if (!cancelled) {
        setSession(null);
      }
    });

    void (async () => {
      try {
        const restored = await authApi.refresh();

        if (!cancelled) {
          apply(restored);
        }
      } catch {
        // Sem sessão é o caso normal de quem chega deslogado. Não é erro.
        if (!cancelled) {
          apply(null);
        }
      } finally {
        if (!cancelled) {
          setIsRestoring(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      setSessionLostHandler(null);
    };
  }, [apply]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const opened = await authApi.login(email, password, describeDevice());
      apply(opened);

      return opened;
    },
    [apply],
  );

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // O estado local é limpo mesmo se a chamada falhar: deixar o usuário
      // "logado" na interface depois de ele pedir para sair é pior que
      // divergir do servidor por um instante.
      apply(null);
    }
  }, [apply]);

  const value = useMemo<AuthState>(
    () => ({ session, isRestoring, signIn, signOut }),
    [session, isRestoring, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Nome legível do dispositivo, para a tela de sessões.
 *
 * É dado de exibição, nunca de decisão — vem do cliente e não pode ser
 * confiado. O servidor registra também o IP, que ele mesmo observa.
 */
function describeDevice(): string {
  const agent = navigator.userAgent;

  const browser = agent.includes('Edg/')
    ? 'Edge'
    : agent.includes('Chrome/')
      ? 'Chrome'
      : agent.includes('Firefox/')
        ? 'Firefox'
        : agent.includes('Safari/')
          ? 'Safari'
          : 'Navegador';

  const platform = agent.includes('Windows')
    ? 'Windows'
    : agent.includes('Android')
      ? 'Android'
      : /iPhone|iPad/.test(agent)
        ? 'iOS'
        : agent.includes('Mac OS')
          ? 'macOS'
          : agent.includes('Linux')
            ? 'Linux'
            : 'Desconhecido';

  return `${browser}/${platform}`;
}
